import {
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useIntercom } from 'react-use-intercom';
import {
  AssistantConversationOutcome,
  IAssistantMessage,
  IAssistantNewConversationRequest,
  PermissionLevel,
} from 'shared';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { Drawer } from '../../../common/Atoms/Drawer';
import { Button } from '../../../common/Atoms/Button';
import { Heading } from '../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import {
  useApplyAssistantProposalMutation,
  useDismissAssistantProposalMutation,
  useGetAssistantConversationByIdQuery,
  useGetAssistantConversationQuery,
  useGiveAssistantConsentMutation,
  useNewAssistantConversationMutation,
  useSendAssistantMessageMutation,
} from '../../services/api/assistantApi/assistant';
import { useUser } from '../../hooks/useUser';
import { useAssistantPageContext } from '../../hooks/useAssistantPageContext';
import { RootState } from '../../store';
import { clearInitialPrompt, close } from '../../slices/assistantDrawer';
import { Spinner } from '../Common/Spinner';

import { AssistantMessage } from './AssistantMessage';
import { AssistantHistory } from './AssistantHistory';
import { AssistantFinishChat, outcomeLabels } from './AssistantFinishChat';

const examplePrompts = [
  `Send a statement to all customers on the 1st of every month`,
  `What late fee policies do I have set up?`,
  `Why hasn't my oldest overdue invoice had a reminder?`,
  `Draft a friendly email chasing my most overdue customer`,
];

function getErrorMessage(error: unknown, fallbackMessage: string) {
  const data = (error as { error?: { data?: unknown } } | null)?.error?.data;

  if (typeof data === `string` && data.trim()) {
    return data;
  }

  if (typeof data === `object` && data) {
    const { message, error: errorText } = data as { message?: string; error?: string };

    return message || errorText || fallbackMessage;
  }

  return fallbackMessage;
}

/** Global AI assistant chat. Opened via the `assistantDrawer` slice; gated on the AI_ASSISTANT org flag in Root */
export function AssistantDrawer() {
  const { open: isOpen, initialPrompt } = useSelector((state: RootState) => state.assistantDrawer);
  const dispatch = useDispatch();
  const currentOrg = useGetSelectedOrganisation();
  const { data: user } = useUser();
  const { show: showIntercom } = useIntercom();
  const organisationId = currentOrg?.id;
  // Users must consent to AI data use before chatting; admin override sessions bypass the gate but are warned
  const missingConsent = !!currentOrg && currentOrg.aiConsentGiven !== true;
  const consentGate = missingConsent && !user?.adminOverride;
  // POST assistant/consent needs settings editor, so viewers get told who to ask instead of a button that 403s
  const canConsent = !!currentOrg?.users?.some(orgUser => orgUser.id === user?.id && orgUser.permissionSettings === PermissionLevel.EDITOR);
  const [giveConsent, consentState] = useGiveAssistantConsentMutation();
  const [draft, setDraft] = useState(``);
  // The policy page behind the drawer; the user can drop it per page
  const pageContext = useAssistantPageContext();
  const contextKey = pageContext ? `${pageContext.type}:${pageContext.id}` : null;
  const [droppedContextKey, setDroppedContextKey] = useState<string | null>(null);
  const activeContext = pageContext && droppedContextKey !== contextKey ? pageContext : null;
  // null = current chat, `history` = list of past chats, number = viewing that past chat
  const [view, setView] = useState<null | `history` | number>(null);
  // Finish chat swaps the composer for the rating panel
  const [finishing, setFinishing] = useState(false);
  // The chat the user just finished and rated, shown with its outcome until they start a new one
  const [ended, setEnded] = useState<{ id: number; outcome: AssistantConversationOutcome } | null>(null);
  const showingEnded = ended && view === ended.id ? ended : null;
  const listRef = useRef<HTMLDivElement | null>(null);

  const [polling, setPolling] = useState(false);
  const { data: activeConversation, isLoading: activeLoading } = useGetAssistantConversationQuery(
    { organisationId: organisationId as string },
    // Turns run on a worker; poll until the running placeholder is filled in
    { skip: !organisationId || !isOpen || consentGate, pollingInterval: polling ? 1500 : 0 },
  );
  const viewingPast = typeof view === `number`;
  // Admin override sessions can use the assistant on any org but never apply changes (enforced server-side too)
  const readOnly = viewingPast
    ? `Not applied. This chat has ended, so ask the assistant again in your current chat to apply it.`
    : user?.adminOverride ? `Not applied. Admin override sessions can't apply changes.` : undefined;
  const { data: pastConversation, isLoading: pastLoading } = useGetAssistantConversationByIdQuery(
    { organisationId: organisationId as string, conversationId: view as number },
    { skip: !organisationId || !viewingPast },
  );
  const conversation = viewingPast ? pastConversation : activeConversation;
  const isLoading = viewingPast ? pastLoading : activeLoading;
  const [sendMessage, sendState] = useSendAssistantMessageMutation();
  const [newConversation, newState] = useNewAssistantConversationMutation();
  const [applyProposal, applyState] = useApplyAssistantProposalMutation();
  const [dismissProposal, dismissState] = useDismissAssistantProposalMutation();

  const messages = conversation?.messages ?? [];
  const runningMessage = activeConversation?.messages.find(message => message.status === `running`);
  const isSending = sendState.isLoading || !!runningMessage;
  const busy = isSending || newState.isLoading || applyState.isLoading || dismissState.isLoading;

  useEffect(() => {
    setPolling(!!runningMessage);
  }, [runningMessage?.id]);

  useEffect(() => {
    if (initialPrompt !== null) {
      setDraft(initialPrompt);
      setView(null);
      dispatch(clearInitialPrompt());
    }
  }, [initialPrompt]);

  const lastMessage = messages[messages.length - 1];
  // The model says what a good next message looks like, e.g. "Type a group name" after a question
  const inputPlaceholder = (lastMessage?.role === `assistant` && lastMessage.status === `done` && lastMessage.chatInputPlaceholder)
    || `Ask a question or describe what you want to do...`;

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length, lastMessage?.status, lastMessage?.events.length]);

  async function send(content: string) {
    if (!organisationId || !content.trim() || busy) {
      return;
    }

    setDraft(``);

    try {
      await sendMessage({
        organisationId,
        content: content.trim(),
        ...(activeContext ? { context: { type: activeContext.type, id: activeContext.id } } : {}),
      }).unwrap();
    }
    catch (error) {
      setDraft(content);
      toast.error(getErrorMessage(error, `Could not send your message.`));
    }
  }

  async function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === `Enter` && !event.shiftKey) {
      event.preventDefault();
      await send(draft);
    }
  }

  async function onApply(message: IAssistantMessage) {
    if (!organisationId) {
      return;
    }

    try {
      await applyProposal({ organisationId, messageId: message.id }).unwrap();
      toast.success(`Changes applied. Actions will be recalculated shortly.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not apply the changes.`));
    }
  }

  async function onDismiss(message: IAssistantMessage) {
    if (!organisationId) {
      return;
    }

    try {
      await dismissProposal({ organisationId, messageId: message.id }).unwrap();
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not dismiss the proposal.`));
    }
  }

  async function onGiveConsent() {
    if (!organisationId) {
      return;
    }

    try {
      await giveConsent({ organisationId }).unwrap();
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not record your consent.`));
    }
  }

  async function onFinish(rating: IAssistantNewConversationRequest) {
    if (!organisationId || !activeConversation) {
      return;
    }

    const finishedId = activeConversation.id;

    try {
      await newConversation({ organisationId, ...rating }).unwrap();
      setFinishing(false);
      setEnded(rating.outcome ? { id: finishedId, outcome: rating.outcome } : null);
      setView(rating.outcome ? finishedId : null);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not finish the chat.`));
    }
  }

  return (
    <Drawer
      open={ isOpen }
      setOpen={ () => dispatch(close()) }
      size={ `lg` }
      bare
    >
      <div className={ `flex h-full flex-col bg-slate-50` }>
        <div className={ `flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3` }>
          <div className={ `flex items-center gap-2` }>
            <SparklesIcon className={ `h-5 w-5 text-indigo-500` } />
            <div>
              <Heading size={ `md` }>{ `Paidnice Assistant` }</Heading>
              <Paragraph
                size={ `xs` }
                variant={ `secondary` }
              >
                { `Ask about your account, set up policies, or draft an email to a customer.` }
                { conversation?.costUsd !== undefined && ` Cost so far: $${conversation.costUsd.toFixed(4)}` }
              </Paragraph>
            </div>
          </div>
          <div className={ `flex items-center gap-2` }>
            <Button
              size={ `sm` }
              color={ `gray` }
              onClick={ () => {
                dispatch(close());
                showIntercom();
              } }
            >
              <ChatBubbleLeftRightIcon className={ `mr-1 inline h-4 w-4` } />
              { `Talk to a human` }
            </Button>
            {
              !consentGate && view !== null &&
              <Button
                size={ `sm` }
                color={ `gray` }
                onClick={ () => {
                  setEnded(null);
                  setView(view === `history` ? null : `history`);
                } }
              >
                <ArrowLeftIcon className={ `mr-1 inline h-4 w-4` } />
                { view === `history` ? `Current chat` : `History` }
              </Button>
            }
            {
              !consentGate && view === null &&
              <Button
                size={ `sm` }
                color={ `gray` }
                onClick={ () => setView(`history`) }
              >
                <ClockIcon className={ `mr-1 inline h-4 w-4` } />
                { `History` }
              </Button>
            }
            {
              !consentGate && view === null &&
              <Button
                size={ `sm` }
                disabled={ busy || finishing || !activeConversation?.messages.length }
                onClick={ () => setFinishing(true) }
              >
                <CheckCircleIcon className={ `mr-1 inline h-4 w-4` } />
                { `Finish chat` }
              </Button>
            }
            <button
              type={ `button` }
              className={ `rounded-md p-1 text-gray-400 hover:text-gray-600` }
              onClick={ () => dispatch(close()) }
            >
              <span className={ `sr-only` }>{ `Close` }</span>
              <XMarkIcon className={ `h-5 w-5` } />
            </button>
          </div>
        </div>

        {
          missingConsent && user?.adminOverride &&
          <div className={ `flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2` }>
            <ExclamationTriangleIcon className={ `h-4 w-4 text-amber-700` } />
            <Paragraph
              size={ `xs` }
              className={ `!text-amber-900` }
            >
              { `This organisation has not consented to its data being used with AI. Only a user of the organisation can give consent.` }
            </Paragraph>
          </div>
        }
        {
          consentGate &&
          <div className={ `flex flex-1 items-center justify-center p-6` }>
            <div className={ `w-full max-w-md rounded-2xl border border-indigo-100 bg-white p-6 text-center shadow-xs` }>
              <ShieldCheckIcon className={ `mx-auto mb-3 h-10 w-10 text-indigo-500` } />
              <Heading
                size={ `md` }
                className={ `mb-2` }
              >
                { `Consent needed to use the assistant` }
              </Heading>
              <Paragraph className={ `mb-4` }>
                { `We take AI and your data seriously, so we need your permission before the assistant can use it.` }
              </Paragraph>
              {
                canConsent
                  ? (
                    <Button
                      loading={ consentState.isLoading }
                      disabled={ consentState.isLoading }
                      onClick={ onGiveConsent }
                    >
                      { `I consent on behalf of my organisation` }
                    </Button>
                  )
                  : (
                    <Paragraph
                      size={ `xs` }
                      variant={ `secondary` }
                    >
                      { `Only a user with settings editor access can give consent. Please ask one to open the assistant and consent.` }
                    </Paragraph>
                  )
              }
            </div>
          </div>
        }
        {
          !consentGate && view === `history` &&
          <AssistantHistory
            organisationId={ organisationId }
            activeConversationId={ activeConversation?.id }
            onSelect={ conversationId => setView(conversationId) }
          />
        }
        {
          viewingPast && !showingEnded &&
          <div className={ `flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2` }>
            <ClockIcon className={ `h-4 w-4 text-amber-700` } />
            <Paragraph
              size={ `xs` }
              className={ `!text-amber-900` }
            >
              { `You are viewing a past chat. It cannot be continued.` }
            </Paragraph>
          </div>
        }
        <div
          ref={ listRef }
          className={ `flex-1 space-y-4 overflow-y-auto p-4 ${view === `history` || consentGate ? `hidden` : ``}` }
        >
          { isLoading && <Spinner /> }
          {
            !isLoading && !viewingPast &&
            <div className={ `flex justify-start` }>
              <div className={ `w-full max-w-[95%] rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs text-slate-700` }>
                <span className={ `font-medium text-indigo-900` }>{ `Early preview. ` }</span>
                { `Ask about anything in your Paidnice account, or ask it to make a change. It can get things wrong, so check its answers. Nothing changes or gets sent until you confirm.` }
              </div>
            </div>
          }
          {
            !isLoading && !messages.length && !viewingPast &&
            <div className={ `rounded-xl border border-dashed border-slate-300 bg-white p-4` }>
              <Paragraph className={ `mb-3` }>{ `Try asking:` }</Paragraph>
              <div className={ `flex flex-wrap gap-2` }>
                {
                  examplePrompts.map(prompt => (
                    <button
                      key={ prompt }
                      type={ `button` }
                      onClick={ () => setDraft(prompt) }
                      className={ `rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50` }
                    >
                      { prompt }
                    </button>
                  ))
                }
              </div>
            </div>
          }
          {
            messages.map((message, index) => (
              <AssistantMessage
                key={ message.id }
                message={ message }
                busy={ busy }
                readOnly={ readOnly }
                onApply={ onApply }
                onDismiss={ onDismiss }
                onAnswer={ !viewingPast && message.id === lastMessage?.id ? send : undefined }
                answer={ messages[index + 1]?.role === `user` ? messages[index + 1].content : undefined }
              />
            ))
          }
          {
            showingEnded && !isLoading &&
            <div className={ `flex items-center gap-3 py-2` }>
              <div className={ `h-px flex-1 bg-slate-200` } />
              <span className={ `text-xs text-slate-500` }>
                { `Chat ended · recorded as ` }
                <strong className={ `font-semibold text-paidniceGreen-800` }>{ outcomeLabels[showingEnded.outcome] }</strong>
              </span>
              <div className={ `h-px flex-1 bg-slate-200` } />
            </div>
          }
        </div>

        {
          showingEnded &&
          <div className={ `flex items-center justify-between gap-3 border-t border-slate-200 bg-white p-4` }>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { `This chat has ended.` }
            </Paragraph>
            <Button
              onClick={ () => {
                setEnded(null);
                setView(null);
              } }
            >
              { `Start a new chat` }
            </Button>
          </div>
        }

        {
          finishing && view === null && !consentGate &&
          <AssistantFinishChat
            loading={ newState.isLoading }
            onBack={ () => setFinishing(false) }
            onSubmit={ onFinish }
          />
        }

        <div className={ `border-t border-slate-200 bg-white p-4 ${view !== null || consentGate || finishing ? `hidden` : ``}` }>
          {
            activeContext &&
            <div className={ `mb-2 flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs text-indigo-900` }>
              <DocumentTextIcon className={ `h-4 w-4 shrink-0 text-indigo-500` } />
              <span className={ `min-w-0 flex-1 truncate` }>
                { `The assistant can see the page you are on: ` }
                <span className={ `font-medium` }>{ activeContext.label }</span>
              </span>
              <button
                type={ `button` }
                aria-label={ `Stop sharing this page with the assistant` }
                className={ `rounded p-0.5 text-indigo-400 hover:text-indigo-700` }
                onClick={ () => setDroppedContextKey(contextKey) }
              >
                <XMarkIcon className={ `h-4 w-4` } />
              </button>
            </div>
          }
          {
            pageContext && !activeContext &&
            <button
              type={ `button` }
              className={ `mb-2 text-xs text-indigo-600 hover:underline` }
              onClick={ () => setDroppedContextKey(null) }
            >
              { `Share ${pageContext.label} with the assistant` }
            </button>
          }
          <textarea
            rows={ 3 }
            value={ draft }
            onChange={ event => setDraft(event.target.value) }
            onKeyDown={ onKeyDown }
            disabled={ busy }
            placeholder={ inputPlaceholder }
            className={ `block w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 shadow-xs focus:border-paidniceGreen-500 focus:outline-hidden focus:ring-2 focus:ring-paidniceGreen-500/20 disabled:cursor-not-allowed disabled:bg-slate-100` }
          />
          <div className={ `mt-3 flex items-center justify-between` }>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { `Enter to send, Shift+Enter for a newline. AI can make mistakes: check every proposal before you apply it.` }
            </Paragraph>
            <Button
              disabled={ busy || !draft.trim() }
              loading={ isSending }
              onClick={ () => send(draft) }
            >
              { `Send` }
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
